import type { Fb } from '~/composables/useFirebase'
import type { CollectionKind } from '~/composables/useCollection'
import type { LifetimeStats } from '~/composables/useLifetimeStats'
import { PROGRESS_EPOCH, PROGRESS_KEYS } from '~/utils/progressEpoch'
import { mergeMetaDocs } from '~/utils/metaMerge'
import { onAppStateChange } from '~/composables/useAppLifecycle'

/**
 * ☁️ Meta-progress cloud sync (v1.17, per Javi: "shouldn't my browser
 * show ascension 9 too?"). Signing in ties the four meta stores —
 * ascension unlocks, stardust, owned rares, the codex — to
 * users/{uid}, so every signed-in device converges.
 *
 * Merge policy: progress only grows. Numbers take the max, lists take
 * the union; the merged result is adopted locally AND pushed back up.
 * Two exceptions earn their keep (v3.61): stardust travels as EARNED and
 * SPENT counters — both grow-only — because max-merging a balance made
 * spending refundable across devices; and favorites are last-writer-wins,
 * because a union would make unstarring impossible.
 * Run saves stay device-local on purpose — two devices mid-run is a
 * conflict, meta progress never is.
 *
 * 👤 v7.88 (Javi, 13 Oct): the stores are the DEVICE's. When one Google
 * account signed out and another signed in on the same phone, the second
 * account's first pull merged everything the device remembered of the
 * first into the second's document. An account switch now clears the
 * stores before that pull (forgetDevice), and the outgoing account's last
 * change goes up before it signs out (useAuth.signOutUser awaits
 * useCloudSync().settle()).
 */

/** 👤 v7.88: the run layer's record of the account this device last agreed
 * its counters with (useRunSync's IDENTITY_KEY). Read here, never written:
 * it says who was signed in before a reload, when this module's own memory
 * is empty. */
const RUN_IDENTITY_KEY = 'elemental-dispatch-run-identity'

/**
 * 👤 v7.88: the progress keys an account switch leaves alone, and why. The
 * rest of PROGRESS_KEYS - the synced stores, the ledgers the achievements
 * read, the display markers that go with them - is cleared (forgetDevice).
 */
const KEPT_ON_SWITCH = new Set<string>([
  // The run layer's: useRunSync.onIdentity parks the outgoing account's
  // campaign under its uid (`run-parked:<uid>`), restores it when that
  // account returns and re-agrees the counters. Its watcher runs right
  // after this one and must find the slot as it was.
  'elemental-dispatch-run-v6',
  'elemental-dispatch-run-rev',
  'elemental-dispatch-run-base',
  'elemental-dispatch-run-identity',
  'elemental-dispatch-run-parked',
  // Device-local run slots by design: the weekly, the campaign stashed by
  // the Puzzle Game or the practice, and which slot was active.
  'elemental-dispatch-daily-v6',
  'elemental-dispatch-active-slot',
  'elemental-dispatch-run-stash-v1',
  'elemental-dispatch-practice-stash-v1',
  // The wallet's: the server ledger is the balance (useStardust.onSignedOut
  // drops the cached one at sign-out), the legacy counters are the one-time
  // import's source and the outbox is flushed before sign-out.
  'elemental-dispatch-stardust',
  'elemental-dispatch-stardust-earned',
  'elemental-dispatch-stardust-spent',
  'elemental-dispatch-wallet',
  'elemental-dispatch-wallet-outbox',
  // The Starweave's caches are keyed per uid inside (useStarweave.onIdentityChanged).
  'elemental-dispatch-starweave-account-v1',
  'elemental-dispatch-starweave-pending-v1',
  'elemental-dispatch-starweave-local-v1',
  // The device's first-time markers - the practice done, the welcome's
  // answer, the first-open cards: device-local by design (useOnboarding),
  // and the flag store keeps session copies a storage sweep cannot reach.
  'elemental-dispatch-tutorial-done',
  'elemental-dispatch-onboarding-v1',
  'elemental-dispatch-first-steps-v1',
  'elemental-dispatch-starweave-tutorial',
  'elemental-dispatch-puzzle-tip',
])

interface Identity { uid: string; anonymous: boolean }

function recordedIdentity(): Identity | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(RUN_IDENTITY_KEY)
    const parsed = raw ? JSON.parse(raw) as { uid?: unknown; anonymous?: unknown } : null
    return parsed && typeof parsed.uid === 'string' ? { uid: parsed.uid, anonymous: parsed.anonymous === true } : null
  } catch {
    return null
  }
}

/** 👤 v7.88: set by initCloudSync; useAuth's sign-out awaits it. */
let settleNow: (() => Promise<boolean>) | null = null

/** 👤 v7.88: the sync's one handle for the rest of the app. */
export function useCloudSync() {
  return {
    /** Ship any unsent meta change NOW and wait for it - the last chance
     * before an account leaves this device. True when the cloud holds what
     * this device holds (and when nobody is signed in). */
    settle(): Promise<boolean> {
      return settleNow ? settleNow() : Promise.resolve(true)
    },
  }
}

export function initCloudSync() {
  const user = useAuthUser()
  const ascension = useAscension()
  const stardust = useStardust()
  const owned = useOwnedCreatures()
  const collection = useCollection()
  const puzzle = usePuzzleGame()
  const favorites = useFavorites()
  // 🏆 v6.45: the achievements ledger rides along (union), so the
  // titles it grants follow the account to every device.
  const achievements = useAchievements()
  // 👤 v7.88: two device ledgers the switch empties. Never synced, but the
  // achievements read them (runs won, the daily streak): one banked from
  // the previous account's ledger would sync up as the new account's.
  const lifetime = useLifetimeStats()
  const daily = useDailyGame()

  let pushTimer: ReturnType<typeof setTimeout> | null = null
  let pulling = false
  /** 👤 v7.88: the pull in flight, so settle() can wait for it. */
  let pullFlight: Promise<void> | null = null
  /** 👤 v7.88: whose picture the stores hold - the last account that signed
   * in this session, else what the device recorded. Null on a device that
   * never signed in (or from before v7.15): nothing to switch from. */
  let heldBy: Identity | null = null
  // 🪦 v6.20: sessions that predate an account reset must never
  // push their stale memory back up. Compared against the tombstone.
  const sessionStart = Date.now()
  /** 🪦 v6.20 latch - v7.19 (v7.18 review): per ACCOUNT, not per session; a
   * reset seen on one account used to silence the next one signing in. */
  let pushDisabledFor: string | null = null
  /** ☁️ v7.19 (v7.18 review #5): a push failed (offline, say) and owes a
   * retry - a transaction cannot queue offline the way the old blind
   * write did. Retried when the network returns and on foreground. */
  let dirty = false
  /**
   * ☁️ v7.17 (follow-up review): up while absorb() writes the stores on
   * the cloud's behalf, and for the watcher flush that follows (cleared
   * on nextTick). The deep watcher below reads any store change as local
   * progress; v7.16 had every push absorb first, and two adopters
   * replaced their reactive value even when nothing had changed - so an
   * idle signed-in session pushed every two seconds, forever.
   */
  let absorbing = false

  function snapshot(fb: Fb) {
    return {
      // 🧨 v3.79: the epoch rides WITH the meta doc. A device that has
      // been wiped stamps the current epoch here, so any other device
      // still holding pre-wipe data sees a newer epoch on pull and
      // discards its own rather than uploading it back.
      epoch: PROGRESS_EPOCH,
      ascensionUnlocked: ascension.unlocked.value,
      // 🏅 v3.62: levels already cleared, so the first-clear bounty is paid
      // once per level across the whole account, not once per device.
      ascensionCleared: ascension.cleared.value,
      // 🗂️ v4.93: ownedCreatures left this snapshot — the server-side
      // ownership doc is the collection now (audit E6); the pull below
      // still READS old devices' lists for the one-time import.
      collection: collection.collection.value,
      collectionMastered: collection.mastered.value,
      // 🧩 v3.22: puzzle bests sync too (union, lowest damage wins).
      puzzleBest: puzzle.best.value,
      // 🏦 v4.92: stardust does NOT sync here anymore — the server-side
      // wallet ledger is the balance (audit E1/E3). The legacy counters
      // are still READ on pull (below) so the one-time import sees a
      // pre-ledger device's earnings, but this client never writes them.
      // ⭐ v3.61: last-writer-wins, so unstarring propagates.
      favorites: favorites.favorites.value,
      favoritesAt: favorites.changedAt.value,
      achievements: achievements.earned.value,
      updatedAt: fb.fs.serverTimestamp(),
    }
  }

  async function push() {
    const u = user.value
    if (!u || pulling || pushDisabledFor === u.uid) return
    try {
      const fb = await firebaseReady()
      const ref = fb.fs.doc(fb.db, 'users', u.uid)
      let resetSeen = false
      // ☁️ v7.17 (follow-up review): ONE transaction reads the live copy,
      // merges this device's snapshot into it (mergeMetaDocs: pure,
      // grow-only) and writes the result. Another device pushing at the
      // same moment makes Firestore retry this with the fresher copy, so
      // the cloud never loses either side. v7.16 did read, merge, write as
      // three steps: two devices could read the same old document and
      // overwrite one another.
      const committed = await fb.fs.runTransaction(fb.db, async (tx) => {
        const existing = await tx.get(ref)
        const d = existing.exists() ? existing.data() as Record<string, unknown> : null
        // 🪦 v6.20: respect a reset tombstone. If the account was wiped
        // AFTER this session booted, this session's memory is stale by
        // definition - latch silent until the next reload.
        const resetAt = d?.resetAt as { toMillis?: () => number } | undefined
        if (typeof resetAt?.toMillis === 'function' && resetAt.toMillis() > sessionStart) {
          resetSeen = true
          return null
        }
        const local = snapshot(fb)
        const merged = d && Number(d.epoch ?? 0) >= PROGRESS_EPOCH ? mergeMetaDocs(local, d) : local
        // 🪦 v7.28 (review #6): a reset marker OLDER than this session is
        // ours to keep, not to erase - tx.set replaces the document, and
        // v6.20-v7.27 dropped the marker on the first post-reset push, so
        // a session from before the reset could then upload the wiped
        // progress. The rules accept the field only when it equals what
        // the document already holds.
        if (d && 'resetAt' in d) (merged as Record<string, unknown>).resetAt = d.resetAt
        tx.set(ref, merged)
        return merged
      })
      if (resetSeen) {
        pushDisabledFor = u.uid
        console.info('Account was reset after this session started - cloud pushes disabled until reload.')
        return
      }
      // What the cloud now holds is what this device holds - adopted under
      // the guard, so the watcher does not read it as new local progress.
      if (committed) absorb(committed as unknown as Record<string, unknown>)
      dirty = false
    } catch (error) {
      dirty = true
      console.warn('Cloud sync push failed - it will be retried when the connection is back.', error)
    }
  }

  /** Grow-only merge of a cloud document into the local stores (numbers
   * take the max, lists union, puzzle bests the min, favorites by time).
   * v7.16: shared by the sign-in pull and every push. */
  function absorb(d: Record<string, unknown>) {
    absorbing = true
    ascension.adoptUnlocked(Number(d.ascensionUnlocked ?? 0))
    if (Array.isArray(d.ascensionCleared)) ascension.adoptCleared(d.ascensionCleared as number[])
    stardust.adopt({
      earned: typeof d.stardustEarned === 'number' ? d.stardustEarned : undefined,
      spent: typeof d.stardustSpent === 'number' ? d.stardustSpent : undefined,
    })
    if (Array.isArray(d.favorites)) {
      favorites.adopt(d.favorites as string[], Number(d.favoritesAt ?? 0))
    }
    if (Array.isArray(d.ownedCreatures)) owned.adopt(d.ownedCreatures as string[])
    if (d.collection && typeof d.collection === 'object') {
      collection.adopt(d.collection as Partial<Record<CollectionKind, string[]>>)
    }
    if (d.collectionMastered && typeof d.collectionMastered === 'object') {
      collection.adoptMastered(d.collectionMastered as Partial<Record<CollectionKind, string[]>>)
    }
    if (d.puzzleBest && typeof d.puzzleBest === 'object') {
      puzzle.adopt(d.puzzleBest as Record<string, number>)
    }
    if (Array.isArray(d.achievements)) achievements.adopt(d.achievements as string[])
    // Down once the flush these writes queued has run - taken HERE, after
    // them, so it is chained behind that flush (taken before, it would
    // resolve first and the watcher would see the guard already down).
    void nextTick(() => { absorbing = false })
  }

  /**
   * 👤 v7.88: another Google account is taking this device over. What the
   * stores hold belongs to the account that just left (its last push went
   * up at sign-out) - cleared BEFORE the pull, on the device and in memory,
   * so the new account's cloud copy merges into nothing and the push after
   * it carries nothing but that copy. Runs under `pulling`, so the watcher
   * does not read the clearing as progress.
   *
   * Not the run layer's keys: useRunSync.onIdentity parks the outgoing
   * account's campaign under its uid, restores it when that account
   * returns and re-agrees the counters - its watcher runs right after this
   * one and finds the slot as it was. Not the wallet's, the Starweave's,
   * nor the device's own first-time markers: KEPT_ON_SWITCH says why.
   */
  function forgetDevice() {
    if (typeof localStorage !== 'undefined') {
      for (const key of PROGRESS_KEYS) {
        if (KEPT_ON_SWITCH.has(key)) continue
        try {
          localStorage.removeItem(key)
        } catch { /* private mode - nothing was kept there */ }
      }
    }
    // Each store's memory: reloaded where the loader re-reads the device
    // (and now finds nothing), emptied in place where it runs once a session.
    ascension.load()
    puzzle.load()
    owned.reset()
    const none = (state: Record<CollectionKind, string[]>) => {
      const out = {} as Record<CollectionKind, string[]>
      for (const kind of Object.keys(state) as CollectionKind[]) out[kind] = []
      return out
    }
    collection.collection.value = none(collection.collection.value)
    collection.mastered.value = none(collection.mastered.value)
    collection.viewed.value = []
    favorites.favorites.value = []
    favorites.changedAt.value = 0
    achievements.earned.value = []
    achievements.earnedAt.value = {}
    const tally = () => ({ started: 0, won: 0, lost: 0, abandoned: 0 })
    const ledger: LifetimeStats = {
      runs: { campaign: tally(), weekly: tally(), gauntlet: tally() },
      streaks: {},
      missions: { succeeded: 0, failed: 0, expired: 0 },
      playtimeMs: 0,
      marks: {},
      eventsSeen: [],
    }
    lifetime.stats.value = ledger
    daily.store.value = { today: null, streak: 0, lastDoneDay: null, boosterQueued: 0, daysPlayed: 0, history: {} }
  }

  function schedulePush() {
    if (pushTimer) clearTimeout(pushTimer)
    pushTimer = setTimeout(() => { void push() }, 2000)
  }

  async function pullAndMerge(uid: string, switched = false) {
    pulling = true
    try {
      // 👤 v7.88: another account's device from here on - see forgetDevice.
      if (switched) forgetDevice()
      // Make sure local storage is in state before merging into it.
      ascension.load()
      stardust.load()
      owned.load()
      collection.load()
      puzzle.load()
      favorites.load()
      achievements.load()
      const fb = await firebaseReady()
      const snap = await fb.fs.getDoc(fb.fs.doc(fb.db, 'users', uid))
      if (snap.exists()) {
        const d = snap.data()
        // 🧨 v3.79: a document written before the current epoch is VOID.
        // Merging it would undo the wipe — grow-only merges take the max
        // of each counter, so one stale cloud copy would restore every
        // player's old stardust and unlocks the moment they signed in.
        //
        // Note this SKIPS the merge but must NOT return: the push after
        // the try block is what replaces the stale document with the
        // wiped snapshot. An early return here would leave the old
        // payload sitting in Firestore indefinitely.
        const stale = Number(d.epoch ?? 0) < PROGRESS_EPOCH
        if (stale) console.info('Cloud progress predates the current epoch — discarding it.')
        if (!stale) absorb(d)
      }
    } catch (error) {
      console.warn('Cloud sync pull failed.', error)
    } finally {
      pulling = false
    }
    // The merged (or first-ever) state becomes the cloud copy.
    void push()
    // 🏦 v4.92: with the account's whole pre-ledger picture merged in,
    // the wallet can refresh, run its one-time import, and flush any
    // queued earnings — order matters, so it happens HERE, not on a watch.
    void stardust.onCloudMerged()
  }

  watch(user, (u) => {
    if (!u) return
    const prev = heldBy ?? recordedIdentity()
    const next: Identity = { uid: u.uid, anonymous: u.anonymous === true }
    heldBy = next
    // 👤 v7.88: a Google account taking over from ANOTHER Google account -
    // the same rule useRunSync.onIdentity parks a run by. The guest paths
    // keep merging as designed: the link keeps the uid, and a guest signing
    // into an existing account brings the device's progress along.
    const switched = !!prev && prev.uid !== next.uid && !prev.anonymous && !next.anonymous
    pullFlight = pullAndMerge(u.uid, switched).then(() => { pullFlight = null })
  }, { immediate: true })

  // 👤 v7.88: sign-out's last push (useAuth.signOutUser). Lets a change made
  // this very tick reach the watcher (which is what arms the debounce), waits
  // for a pull in flight, cancels the debounce and ships now.
  settleNow = async () => {
    if (!user.value) return true
    await nextTick()
    if (pullFlight) await pullFlight
    if (pushTimer) { clearTimeout(pushTimer); pushTimer = null }
    await push()
    return !dirty
  }

  // Write-through: any meta change while signed in syncs up shortly.
  // (🏦 v4.92: stardust left the list — the wallet ledger owns it.
  //  🗂️ v4.93: ownedCreatures too — the server collection owns it.)
  watch(
    () => [
      ascension.unlocked.value,
      ascension.cleared.value,
      collection.collection.value,
      collection.mastered.value,
      puzzle.best.value,
      favorites.favorites.value,
      achievements.earned.value,
    ],
    () => {
      if (user.value && !pulling && !absorbing) schedulePush()
    },
    { deep: true },
  )

  // 🏦 v4.92: queued wallet events flush when connectivity returns, and
  // once at boot for the signed-out backlog case (the flush itself mints
  // the lazy anonymous identity only when there's something to send).
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      void stardust.flush()
      if (dirty) void push()
    })
    setTimeout(() => { void stardust.flush() }, 4000)
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && dirty) void push()
    })
  }
  // 📱 M6: the native shell's own trip to the background and back. Leaving
  // ships what is waiting now (the app may never come back); returning
  // retries anything that failed while it was away.
  onAppStateChange((active) => {
    if (!user.value) return
    if (!active && pushTimer) { clearTimeout(pushTimer); pushTimer = null }
    if (dirty) void push()
  })
}
